import React, { useRef, useState, useEffect, useCallback } from "react";
import { Row, Col, Container, Button, Nav, Form, Card } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  getPatientById,
  getAppointmentById,
  getServiceById,
  fetchSessionByAppointmentId,
  getSessionById,
  createSession,
} from "../../api";
import { FollowUpSession } from "./FollowUpSession";
import { UpdateSession } from "./EditSession";
import { PrevSessions } from "../Sessions/PrevSessions";
import { PatientReports } from "../PatientReports/patientReports";
import { LinkNotes } from "./LinkNotes";
import PatientFileManager from "./PatientFiles";
import Diagnosis from "./Diagnosis";
import { useRecording } from "../SyncTo/RecordingProvider";
import RecordModal from "../SyncTo/RecordModal";
import ClinicalHistoryFullPanel from "../ClinicalHistory/ClinicalHistoryFullPanel";
import { userCanSeeClinicalHistory } from "../ClinicalHistory/access";

export default function Session({
  extraMobileMenu = [],
  onMobileNavigate,
  patient: patientProp,
  appointment: appointmentProp,
}) {
  const followUpRef = useRef();
  const creatingRef = useRef(null);
  const { id, appointmentId, sessionId } = useParams();
  const hasAppointment = appointmentId && appointmentId !== "1";
  const editingAdHoc = !!sessionId && !hasAppointment;

  // sub-sections: apptTemplate | apptNotepad | apptMedia
  const [activeSection, setActiveSection] = useState(
    appointmentId === "1" ? "historical" : "apptTemplate",
  );

  // SyncTo modal: mode picker + consent + recording UI live in a single
  // combined RecordModal now (was two-modal flow with ModeModal). The
  // SyncToButton click resolves the session, then opens RecordModal directly
  // — RecordModal owns the mode picker / consent gate internally and PATCHes
  // /consent at the moment Start is clicked.
  const recording = useRecording();

  const [patient, setPatient] = useState(patientProp);
  const [appointment, setAppointment] = useState(appointmentProp);
  const [service, setService] = useState();
  const [existingSessionId, setExistingSessionId] = useState(null);
  const [existingSession, setExistingSession] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isPortrait, setIsPortrait] = useState(true);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth <= 1024);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [isLandscape, setIsLandscape] = useState(window.matchMedia("(orientation: landscape)").matches);
  const isApptSubtab = ["apptTemplate", "apptNotepad", "apptMedia"].includes(
    activeSection,
  );

  const formattedDateTime =
    appointment?.start && !isNaN(new Date(appointment.start))
      ? new Intl.DateTimeFormat("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          day: "2-digit",
          month: "short",
          year: "numeric",
        }).format(new Date(appointment.start))
      : "Invalid Date";

  // Phase 30 — patientLabel passed to SyncToButton → RecordingProvider.openModal(meta)
  // so the IDB sessions row (D-06 / D-08) is populated with a human-readable
  // label for the orphan prompt. Derived from existing `appointment` state —
  // no new fetches.
  const patientFullName = [appointment?.patientFirstName, appointment?.patientLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const patientLabel = patientFullName
    ? `${patientFullName}${formattedDateTime && formattedDateTime !== "Invalid Date" ? ` — ${formattedDateTime}` : ""}`
    : (formattedDateTime && formattedDateTime !== "Invalid Date" ? formattedDateTime : "");

  const NotesHeader = ({ isAdHoc }) => {
    return (
      <div>
        <Row className="align-items-center">
          <Col md={12}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              {isAdHoc ? (
                <p className="text-primary fw-bold mb-0"></p>
              ) : (
                <p className="text-primary fw-bold mb-0">
                  {formattedDateTime} &nbsp;-&nbsp;{" "}
                  {service?.servicesName || "Loading service name..."}
                </p>
              )}
            </div>
          </Col>
        </Row>

      </div>
    );
  };

  const renderAppointmentNotes = () => {
    // --- EDITING AD-HOC (by sessionId) ---
    // Also enters this branch when the session was lazy-created on SyncTo click
    // (existingSession populated but URL lacks :sessionId, so editingAdHoc is
    // false). Without this, post-Apply refetch lands in `existingSession` but
    // FollowUpSession (create-mode) never reads it → UI looks unchanged until
    // page refresh.
    if (editingAdHoc || (!hasAppointment && existingSession && existingSession._id)) {
      return (
        <>
          <NotesHeader isAdHoc />
          {existingSessionId ? (
            <UpdateSession
              key={`edit-adhoc-${existingSessionId}-${existingSession?.updatedAt || ""}`}
              sessionId={existingSession._id}
              appointmentId={existingSession.sessionsAppointmentId || null} // null for ad-hoc
              practiceId={existingSession.sessionsPracticeId}
              patientId={id}
              sections={existingSession.sections}
              notepadContentR={existingSession.notepadContent || ""}
              notepadContentType={existingSession.notepadContentType || ""}
              mediaFilesR={existingSession.mediaFiles || []}
              appointmentStatus={existingSession.appointmentStatus || null}
              serviceName={existingSession.sessionsServiceName || "Ad Hoc"}
              appointmentStart={
                existingSession.sessionsAppointmentStart ||
                existingSession.createdAt
              }
              templateId={existingSession.sessionsTemplateId}
              resourceId={existingSession.sessionsResourceId || null}
              activeSection={activeSection}
              onSaved={() => setActiveSection("historical")}
              capturedValuesR={existingSession.capturedValues || {}}
              sessionsTemplateVersionR={existingSession.sessionsTemplateVersion}
              updatedAtR={existingSession.updatedAt || null}
              syncToProps={syncToProps}
            />
          ) : (
            <p>Loading session…</p>
          )}
        </>
      );
    }

    // --- AD-HOC CREATE (no appointment, no sessionId) ---
    if (!hasAppointment) {
      return (
        <>
          <NotesHeader isAdHoc />
          <FollowUpSession
            key={`adhoc-${id}`}
            ref={followUpRef}
            adHoc
            appointmentId={null}
            patientId={id}
            serviceName={""}
            resourceId={null}
            appointmentStart={null}
            appointmentStatus={null}
            practiceId={undefined}
            activeSection={activeSection}
            onUpdate={() => setActiveSection("historical")}
            syncToProps={syncToProps}
          />
        </>
      );
    }

    // --- APPOINTMENT BRANCH (unchanged) ---
    if (!appointment) return <p>Loading appointment details...</p>;

    // Phase 34 follow-up bug #1 — include updatedAt in the key so a post-apply
    // refetch of existingSession (which appends the [SyncTo] block to
    // notepadContent) forces UpdateSession to remount. Without this, the
    // notepad-content effect inside EditSession.jsx only runs once per
    // sessionId, so the fresh prop is ignored until a full page refresh.
    const upKey = `up-${existingSessionId || "none"}-${existingSession?.updatedAt || ""}`;
    const createKey = `fu-${appointmentId}`;

    // Render UpdateSession only when we have the FULL session doc — when only
    // existingSessionId is populated (e.g. SyncTo lazy-created a session but
    // hasn't refetched the doc yet) we fall through to FollowUpSession so a
    // null `existingSession._id` access doesn't crash render.
    if (existingSessionId && existingSession && existingSession._id) {
      return (
        <>
          <NotesHeader isAdHoc={false} />
          <UpdateSession
            key={upKey}
            sessionId={existingSession._id}
            appointmentId={existingSession.sessionsAppointmentId}
            practiceId={existingSession.sessionsPracticeId}
            patientId={id}
            sections={existingSession.sections}
            notepadContentR={existingSession.notepadContent || ""}
            notepadContentType={existingSession.notepadContentType || ""}
            mediaFilesR={existingSession.mediaFiles || []}
            appointmentStatus={appointment.status}
            serviceName={service?.servicesName || ""}
            appointmentStart={appointment.start}
            templateId={existingSession.sessionsTemplateId}
            resourceId={appointment.resourceId}
            activeSection={activeSection}
            onSaved={() => setActiveSection("historical")}
            capturedValuesR={existingSession.capturedValues || {}}
            sessionsTemplateVersionR={existingSession.sessionsTemplateVersion}
            updatedAtR={existingSession.updatedAt || null}
            syncToProps={syncToProps}
          />
        </>
      );
    }

    return (
      <>
        <NotesHeader isAdHoc={false} />
        <FollowUpSession
          key={createKey}
          ref={followUpRef}
          appointmentId={appointmentId}
          patientId={id}
          serviceName={service?.servicesName || ""}
          resourceId={appointment.resourceId}
          appointmentStart={appointment.start}
          appointmentStatus={appointment.status}
          practiceId={appointment.practiceId}
          activeSection={activeSection}
          onUpdate={() => setActiveSection("historical")}
          syncToProps={syncToProps}
        />
      </>
    );
  };

  const doAndCollapse = (fn, payload) => {
    try {
      fn?.();
    } finally {
      setIsSidebarCollapsed(true);
      onMobileNavigate?.(payload);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
      setIsNarrow(window.innerWidth <= 1024);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const mql = window.matchMedia("(orientation: portrait)");
    const onChange = (e) => setIsPortrait(e.matches ?? mql.matches);
    onChange(mql);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  // Sync prop → state when parent (Patientsappointment) supplies these and
  // they arrive after first paint. Patientsappointment fetches the same docs
  // via its own effects; passing them in lets us skip duplicate API calls.
  // Convention: prop === undefined ⇒ "parent isn't supplying, fetch yourself";
  // prop === null ⇒ "parent is loading, wait"; prop populated ⇒ use it.
  useEffect(() => {
    if (patientProp) setPatient(patientProp);
  }, [patientProp]);

  useEffect(() => {
    if (appointmentProp) {
      setAppointment(appointmentProp);
      setSelectedStatus(appointmentProp.status);
    }
  }, [appointmentProp]);

  useEffect(() => {
    async function loadPatientAndAppointment() {
      try {
        if (!appointmentId) return;

        const parentSuppliesPatient = patientProp !== undefined;
        const parentSuppliesAppointment = appointmentProp !== undefined;

        if (!parentSuppliesPatient) {
          const patientData = await getPatientById(id);
          setPatient(patientData);
        }

        if (!parentSuppliesAppointment) {
          const appointmentData = await getAppointmentById(appointmentId);
          setAppointment(appointmentData);
          setSelectedStatus(appointmentData.status);
        }

        // ensure correct default tab
        setActiveSection(appointmentId === "1" ? "historical" : "apptTemplate");
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadPatientAndAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, appointmentId]);

  // Service lookup runs whenever appointment.serviceId becomes available —
  // whether appointment came from props or our own fetch.
  useEffect(() => {
    if (!appointment?.serviceId) return;
    let cancelled = false;
    getServiceById(appointment.serviceId)
      .then((s) => { if (!cancelled) setService(s); })
      .catch((err) => console.error("Error fetching service:", err));
    return () => { cancelled = true; };
  }, [appointment?.serviceId]);

  useEffect(() => {
    async function loadSession() {
      try {
        if (editingAdHoc && sessionId) {
          const s = await getSessionById(sessionId); // <- implement in ../../api if needed
          if (s && s._id) {
            setExistingSessionId(s._id);
            setExistingSession(s);
          }
          setIsLoading(false);
          return;
        }
        // existing appointment-based lookup. Skip when appointmentId is the
        // "1" sentinel (ad-hoc / historical) because /sessions/appointment/1
        // is a 404 — sessions for those flows are either created lazily on
        // SyncTo click or edited via the editingAdHoc branch above.
        if (hasAppointment) {
          const s = await fetchSessionByAppointmentId(appointmentId);
          if (s && s._id) {
            setExistingSessionId(s._id);
            setExistingSession(s);
          }
        } else {
          // "Add Note" / historical view — clear any stale existingSession
          // left over from a previous appointment-bound render so clicking
          // Add Note opens a fresh FollowUpSession (create mode) instead of
          // UpdateSession (edit mode) with the previous session's values.
          setExistingSessionId(null);
          setExistingSession(null);
        }
      } catch (error) {
        console.error("Error fetching session:", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadSession();
  }, [appointmentId, sessionId, editingAdHoc]);

  useEffect(() => {
    if (appointment) setSelectedStatus(appointment.status);
  }, [appointment]);

  useEffect(() => {
    if (!isMobile && activeSection === "apptMedia") {
      setActiveSection("apptTemplate");
    }
  }, [isMobile, activeSection]);

  // Phase 32 — SyncTo Apply post-200 handler.
  // Switches to the Note Pad tab and refetches the session so the appended
  // [SyncTo <ISO>] block renders via the existing notepadContent path.
  // D-08 / D-08a — refetch via existing endpoint; no new socket event.
  const onSyncToApplied = useCallback(async ({ mode } = {}) => {
    if (mode === 'note') {
      setActiveSection('apptNotepad');
    }
    if (mode === 'template') {
      // Phase 33 — Template Apply success switches to the appointment template
      // tab so the doctor immediately sees the written capturedValues rendered
      // via EditSession → NoteRenderer (readonly mode). Refetch path below is
      // shared with the Note branch so the rendered values are fresh.
      setActiveSection('apptTemplate');
    }
    try {
      let refetched = null;
      if (hasAppointment) {
        refetched = await fetchSessionByAppointmentId(appointmentId);
      } else if (existingSessionId) {
        // Ad-hoc / historical session: appointmentId is the "1" sentinel,
        // so refetch by the known session id instead (bug from Phase 32
        // exposed by Phase 34's CORS fix — the appointment route 404s on "1").
        refetched = await getSessionById(existingSessionId);
      }
      if (refetched && refetched._id) {
        setExistingSessionId(refetched._id);
        setExistingSession(refetched);
      }
    } catch (_err) {
      // R11 / Q10 mitigation — refetch failure is non-fatal (write already
      // happened server-side). User can manually refresh to see changes.
    }
  }, [appointmentId, hasAppointment, existingSessionId, setActiveSection]);

  // Listen for the global 'syncto:applied' event so this Session refreshes
  // itself even when meta.onApplied is stale (modal opened via socket event
  // or after a notification-click navigation that remounted this Session
  // instance with existingSessionId still null).
  useEffect(() => {
    const handler = async (e) => {
      const detail = (e && e.detail) || {};
      const matchesSession = existingSessionId && detail.sessionId &&
        String(detail.sessionId) === String(existingSessionId);
      const matchesAppointment = hasAppointment && detail.sessionId &&
        existingSession && String(existingSession._id) === String(detail.sessionId);
      const matchesPatient = detail.patientId && id &&
        String(detail.patientId) === String(id);

      // Adopt the event's sessionId when:
      //   - we already track it (matchesSession / matchesAppointment), OR
      //   - we have no session yet but the event is for this patient
      //     (ad-hoc create flow that remounted via the notification click —
      //     existingSessionId is null but we still need to refresh the UI).
      if (!matchesSession && !matchesAppointment && !matchesPatient) return;

      if ((!matchesSession && !matchesAppointment) && matchesPatient && detail.sessionId) {
        try {
          const fresh = await getSessionById(detail.sessionId);
          if (fresh && fresh._id) {
            setExistingSessionId(String(fresh._id));
            setExistingSession(fresh);
            if (detail.mode === 'note') setActiveSection('apptNotepad');
            else if (detail.mode === 'template') setActiveSection('apptTemplate');
            return;
          }
        } catch (_err) { /* fall through to onSyncToApplied */ }
      }
      onSyncToApplied({ mode: detail.mode });
    };
    window.addEventListener('syncto:applied', handler);
    return () => window.removeEventListener('syncto:applied', handler);
  }, [existingSessionId, existingSession, hasAppointment, id, onSyncToApplied]);

  // Phase 32.1 GAP-A — B2 lazy session creation on SyncTo click.
  // When the user clicks SyncToButton and no session exists yet, create a
  // minimal session via POST /sessions/create so ReviewModal's Apply button
  // (disabled on !sessionId) becomes enabled once the recording is reviewed.
  // - Short-circuits if existingSessionId is already populated (no new write).
  // - Branches ad-hoc (URL /1 or editingAdHoc) vs appointment (hasAppointment).
  // - On 201: setExistingSessionId(id); return { sessionId: id }.
  // - On failure: return { sessionId: null, error } so SyncToButton bails out.
  // Consent write is Phase 34 (D-03a); /sessions/create enforces practice access.
  const serviceName = service?.servicesName || '';
  const ensureSessionExists = useCallback(async () => {
    if (existingSessionId) {
      return { sessionId: existingSessionId };
    }
    if (!id) {
      return { sessionId: null, error: new Error('Patient not loaded yet') };
    }
    // WR-01 — in-flight lock: concurrent clicks short-circuit to the same
    // pending Promise so we never POST /sessions/create twice in parallel.
    if (creatingRef.current) {
      return creatingRef.current;
    }
    const isAdHoc = !hasAppointment; // matches Session.jsx's existing hasAppointment logic

    // Appointment branch needs the loaded appointment doc (practiceId,
    // resourceId, start). When the prop hasn't synced yet (e.g. user clicks
    // SyncTo before patientsappointment.jsx's getAppointmentById fetch
    // resolves), fetch it inline rather than failing silently.
    let appt = appointment;
    if (!isAdHoc && !appt && appointmentId) {
      try {
        appt = await getAppointmentById(appointmentId);
      } catch (_err) {
        appt = null;
      }
      if (!appt) {
        alert('Appointment is still loading — please try again in a moment.');
        return { sessionId: null, error: new Error('Appointment data not loaded yet') };
      }
    }

    // Resolve practiceId per-branch. Appointment branch reads appt.practiceId.
    // Ad-hoc branch mirrors FollowUpSession.jsx:1293-1312 — fall back to JWT
    // `decoded.practice[0]` (the exact source the Save path uses).
    let practiceId;
    if (isAdHoc) {
      try {
        const token = sessionStorage.getItem('user');
        if (token) {
          const decoded = jwtDecode(token);
          if (Array.isArray(decoded?.practice) && decoded.practice.length > 0) {
            practiceId = String(decoded.practice[0]);
          }
        }
      } catch (_err) {
        // Malformed token — fall through to the not-resolved error below.
      }
    } else {
      practiceId = appt.practiceId;
    }
    if (!practiceId) {
      alert('Could not resolve practice for this session — please refresh and try again.');
      return { sessionId: null, error: new Error('Practice not resolved') };
    }

    const payload = isAdHoc
      ? {
          sessionsPatientId: id,
          sessionsPracticeId: practiceId,
          sessionsAdHoc: true,
          sessionsAppointmentStart: new Date().toISOString(),
          sessionsServiceName: serviceName || 'Ad Hoc',
        }
      : {
          sessionsPatientId: id,
          sessionsPracticeId: practiceId,
          sessionsAppointmentId: appointmentId,
          sessionsServiceName: serviceName,
          sessionsResourceId: appt.resourceId || '',
          sessionsAppointmentStart: appt.start || new Date().toISOString(),
        };

    const pending = (async () => {
      try {
        // WR-03 — /sessions/create returns `{ message, sessionId, ... }` in both
        // branches (see backend/src/sessionsRoutes.js:284, 377). Pin to the
        // exact field; no defensive fallbacks that mask backend changes.
        const resp = await createSession(payload);
        const newId = resp && resp.sessionId;
        if (!newId) {
          return { sessionId: null, error: new Error('Session creation returned no id') };
        }
        const idStr = String(newId);
        setExistingSessionId(idStr);
        return { sessionId: idStr };
      } catch (err) {
        // Single-attempt (D-07 parity — no retry). User can click SyncTo again.
        // WR-02 — plain alert(); Session.jsx doesn't currently import a toast
        // utility, so the nested try/catch wrapper added no value.
        alert('Could not prepare a session for SyncTo — please try again.');
        return { sessionId: null, error: err };
      }
    })();
    creatingRef.current = pending;
    try {
      return await pending;
    } finally {
      creatingRef.current = null;
    }
  }, [existingSessionId, id, appointment, serviceName, hasAppointment, appointmentId]);

  // SyncTo click interceptor — resolves the session lazily, then opens the
  // combined RecordModal directly. The modal owns the mode picker, template
  // selection, and consent gate internally; PATCH /consent fires when the
  // user clicks Start inside the modal. We still return { sessionId: null }
  // so SyncToButton bails out of its own openModal call (we just made it).
  const onSyncToBeforeOpen = useCallback(async () => {
    const result = await ensureSessionExists();
    if (!result || !result.sessionId) {
      return { sessionId: null, error: result?.error || new Error('Session not ready') };
    }
    recording?.openModal({
      appointmentId,
      patientLabel,
      sessionId: result.sessionId,
      // existingCapturedValues seeds the Template-mode review preview when
      // the user is editing a session that already has captured fields.
      existingCapturedValues: (existingSession && existingSession.capturedValues) || {},
      onApplied: onSyncToApplied,
    });
    return { sessionId: null };
  }, [ensureSessionExists, recording, appointmentId, patientLabel, existingSession, onSyncToApplied]);

  // Phase 34 follow-up — SyncToButton was relocated from Session.jsx's top-level
  // sidebar into the FollowUpSession / UpdateSession nav columns. This bundle
  // threads the Session-owned callbacks (lazy session creation, ModeModal open,
  // post-apply refetch) down so the button still behaves identically.
  const syncToProps = {
    appointmentId,
    patientLabel,
    sessionId: existingSessionId,
    onApplied: onSyncToApplied,
    onBeforeOpen: onSyncToBeforeOpen,
  };

  if (isLoading) return <p>Loading session data...</p>;

  // ------- Sidebar items (add Link Notes after Previous Notes) -------
  const appointmentKeys = isMobile
    ? ["apptTemplate", "apptNotepad", "apptMedia"]
    : ["apptTemplate", "apptNotepad"];

  const parentIsActive = appointmentKeys.includes(activeSection);

  const shortLabels = isLandscape || isNarrow;
  const canSeeClinicalHistory = userCanSeeClinicalHistory();

  const sidebarItems = [
    {
      key: "appointment",
      label: hasAppointment
        ? (shortLabels ? "Appointment" : "Appointment Notes")
        : "Add Note",
      icon: "fa-light fa-note-medical",
    },
    {
      key: "historical",
      label: shortLabels ? "Previous" : "Previous Notes",
      icon: "fa-sharp fa-light fa-notes",
    },
    ...(canSeeClinicalHistory ? [{
      key: "history",
      label: shortLabels ? "History" : "AI History",
      icon: "fa-light fa-clock-rotate-left",
    }] : []),
    {
      key: "diagnosis",
      label: "Diagnoses",
      icon: "fa-sharp fa-light fa-user-doctor-message",
    },
    {
      key: "linkNotes",
      label: shortLabels ? "Link" : "Link Notes",
      icon: "fa-light fa-paperclip",
    },
    {
      key: "files",
      label: shortLabels ? "Files" : "Patient Files",
      icon: "fa-light fa-cabinet-filing",
    },
    {
      key: "reports",
      label: shortLabels ? "Reports" : "Generate Reports",
      icon: "fa-light fa-file-arrow-down",
    },
  ];

  const titleMap = {
    apptTemplate: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptNotepad: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    apptMedia: hasAppointment
      ? "APPOINTMENT NOTES"
      : "AD HOC NOTES",
    historical: "PREVIOUS SESSIONS",
    diagnosis: "DIAGNOSES",
    linkNotes: "LINK NOTES",
    files: "PATIENT FILES",
    reports: "GENERATE REPORTS",
  };

  const renderContent = () => {
    if (["apptTemplate", "apptNotepad", "apptMedia"].includes(activeSection)) {
      return renderAppointmentNotes();
    }
    if (activeSection === "historical") return <PrevSessions patientId={id} />;

    if (activeSection === "linkNotes") {
      return (
        <LinkNotes
          patientId={id}
          patientFirstName={appointment?.patientFirstName}
          patientLastName={appointment?.patientLastName}
          onSuccess={() => setActiveSection("historical")}
        />
      );
    }

    if (activeSection === "files") return <PatientFileManager patientId={id} />;
    if (activeSection === "reports") return <PatientReports patientId={id} />;
    if (activeSection === "diagnosis") {
      return <Diagnosis patientId={id} />;
    }
    if (activeSection === "history" && canSeeClinicalHistory) {
      return (
        <ClinicalHistoryFullPanel
          active
          patientId={id}
          practiceId={
            appointment?.practiceId ||
            existingSession?.sessionsPracticeId ||
            (Array.isArray(patient?.patientPractice) ? patient.patientPractice[0] : undefined)
          }
          patientName={patientFullName || patient?.patientFullNames || ""}
          showHeader={false}
        />
      );
    }

    return null;
  };

  return (
    <div className="ms-1">
      {/* ── MOBILE: collapsible sidebar drawer ── */}
      {isMobile && (
        <>
          {/* Arrow toggle */}
          <div
            className="position-fixed d-flex align-items-center justify-content-center bg-primary text-white shadow-lg"
            style={{
              left: isSidebarCollapsed ? "10px" : "250px",
              top: "50%",
              transform: "translateY(-50%)",
              width: "20px",
              height: "40px",
              borderRadius: "0 8px 8px 0",
              cursor: "pointer",
              transition: "left 0.3s ease",
              zIndex: 1050,
            }}
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          >
            <i
              className={`bi ${isSidebarCollapsed ? "bi-chevron-right" : "bi-chevron-left"}`}
              style={{ fontSize: "18px" }}
            />
          </div>

          {/* Overlay */}
          {!isSidebarCollapsed && (
            <div
              className="position-fixed w-100 h-100 bg-dark bg-opacity-50"
              style={{ top: 0, left: 0, zIndex: 1040 }}
              onClick={() => setIsSidebarCollapsed(true)}
            />
          )}

          {/* Drawer */}
          <div
            className="position-fixed bg-white shadow-lg d-flex flex-column"
            style={{
              left: isSidebarCollapsed ? "-250px" : "0",
              top: "0",
              width: "250px",
              height: "100vh",
              transition: "left 0.3s ease",
              zIndex: 1045,
            }}
          >
            <div className="p-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <span className="fw-bold text-primary">Menu</span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setIsSidebarCollapsed(true)}
                >
                  ×
                </Button>
              </div>
            </div>

            <div className="flex-grow-1 overflow-auto">
              {extraMobileMenu.length > 0 && (
                <>
                  <div className="px-3 pt-3 text-muted small">Patient</div>
                  <div className="p-3">
                    {extraMobileMenu.map((item) => (
                      <div key={item.key} className="mb-2">
                        <Button
                          variant="outline-primary"
                          className="w-100 text-start"
                          onClick={() => doAndCollapse(item.onClick, "patient")}
                        >
                          {item.label}
                        </Button>
                      </div>
                    ))}
                  </div>
                  <hr className="my-0" />
                </>
              )}

              <div className="p-3 pt-3">
                <div className="text-muted small mb-2">Session</div>
                {sidebarItems.map((item) => {
                  const isActive =
                    item.key === "appointment"
                      ? parentIsActive
                      : activeSection === item.key;
                  return (
                    <div key={item.key} className="mb-2">
                      <Button
                        variant={isActive ? "primary" : "outline-primary"}
                        className="w-100 text-start d-flex align-items-center gap-2"
                        onClick={() => {
                          setActiveSection(
                            item.key === "appointment" ? "apptTemplate" : item.key,
                          );
                          doAndCollapse(null, "session");
                        }}
                      >
                        <i className={item.icon} />
                        <span>{item.label}</span>
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── DESKTOP: centered top row of buttons with title pinned left ── */}
      {!isMobile && (
        <div className="d-flex align-items-start mt-2 pb-2">
          {/* Left: title — takes equal share so center is truly centred */}
          <div style={{ flex: "1 1 0", minWidth: 0 }}>
           {/*} <h5 className="text-primary mt-3 mb-0">{titleMap[activeSection] || ""}</h5> */}
          </div>
          {/* Centre: buttons */}
          <div className="d-flex justify-content-center flex-wrap gap-1 mt-3" style={{ flex: "0 1 auto" }}>
            {sidebarItems.map((item, idx) => (
              <React.Fragment key={item.key}>
                {idx === 6 && isNarrow && <div style={{ width: "100%" }} />}
                <Button
                  variant={
                    (item.key === "appointment" ? parentIsActive : activeSection === item.key)
                      ? "primary"
                      : "outline-primary"
                  }
                  className="rounded-0"
                  style={{ minWidth: "120px" }}
                  onClick={() =>
                    setActiveSection(
                      item.key === "appointment" ? "apptTemplate" : item.key,
                    )
                  }
                >
                  <i className={`${item.icon} me-2`} />
                  {item.label}
                </Button>
              </React.Fragment>
            ))}
          </div>
          {/* Right: spacer matching left so buttons stay centred */}
          <div style={{ flex: "1 1 0" }} />
        </div>
      )}

      {/* Content area */}
      <div className="pt-1">
        {renderContent()}
      </div>

      {/* Combined RecordModal — owns mode picker, consent, and recording UI.
          Rendered exactly once per Session mount so it unmounts on in-app
          nav (SC4) while the RecordingProvider state above the router keeps
          the recorder alive. Driven entirely by useRecording() — no props
          required here. */}
      <RecordModal />

    </div>
  );
}
